// src/pages/users/UserDetailPermissions.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import useRoleStore from '../../stores/roleStore';

// Permission resources with descriptions
const PERMISSION_RESOURCES = {
  users: 'Users',
  clients: 'Clients',
  brands: 'Brands',
  content: 'Content',
  campaigns: 'Campaigns',
  templates: 'Templates',
  workflows: 'Workflows',
  finances: 'Finances',
  analytics: 'Analytics',
  reports: 'Reports',
  settings: 'Settings',
  approvals: 'Approvals'
};

const UserDetailPermissions = ({ user }) => {
  // State
  const [roleInfo, setRoleInfo] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get roles from role store
  const { 
    roles, 
    loading: rolesLoading,
    fetchRoles
  } = useRoleStore();
  
  // Fetch roles and user role info
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Fetch roles if needed
      if (roles.length === 0) {
        await fetchRoles();
      }
      
      // Find user's role
      if (user && user.role && roles.length > 0) {
        const role = roles.find(r => r.key === user.role || r.name.toLowerCase() === user.role.toLowerCase());
        setRoleInfo(role);
        
        // Check if user has custom permissions (different from role default)
        if (role && user.permissions) {
          const rolePerms = new Set(role.permissions.map(p => `${p.resource}|${p.action}`));
          const userPerms = new Set(user.permissions.map(p => `${p.resource}|${p.action}`));
          
          // If counts differ or any permission differs, it's custom
          setIsCustom(
            rolePerms.size !== userPerms.size || 
            [...userPerms].some(p => !rolePerms.has(p))
          );
        }
      }
      
      setIsLoading(false);
    };
    
    loadData();
  }, [user, roles]);
  
  // Format role name
  const formatRoleName = (roleKey) => {
    if (!roleKey) return '';
    
    // Find role in roles array
    const roleObj = roles.find(r => r.key === roleKey);
    if (roleObj) return roleObj.name;
    
    // Fallback to formatting the key
    return roleKey
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  if (isLoading || rolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        User Permissions
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary">
          Role: <strong>{formatRoleName(user.role)}</strong>
          {isCustom && (
            <Chip 
              size="small" 
              label="Custom Permissions" 
               
              variant="outlined" 
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        
        {roleInfo?.description && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {roleInfo.description}
          </Typography>
        )}
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {user.permissions && user.permissions.length > 0 ? (
        <Grid container spacing={3}>
          {Object.keys(PERMISSION_RESOURCES).map((resource) => {
            // Get permissions for this resource
            const resourcePermissions = user.permissions.filter(p => p.resource === resource);
            
            // Skip if no permissions for this resource
            if (resourcePermissions.length === 0) return null;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={resource}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {PERMISSION_RESOURCES[resource] || resource}
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    {resourcePermissions.map((permission) => {
                      const { action, scope } = permission;
                      let color;
                      
                      // Set color based on action
                      switch (action) {
                        case 'create':
                          color = 'primary';
                          break;
                        case 'read':
                          color = 'info';
                          break;
                        case 'update':
                          color = 'warning';
                          break;
                        case 'delete':
                          color = 'error';
                          break;
                        default:
                          color = 'default';
                      }
                      
                      return (
                        <Tooltip 
                          key={`${resource}-${action}`}
                          title={scope ? `Scope: ${scope}` : ''}
                        >
                          <Chip 
                            label={action} 
                            size="small" 
                            color={color} 
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5, textTransform: 'capitalize' }} 
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Typography variant="body1" color="textSecondary">
          No specific permissions assigned. User has default permissions based on role.
        </Typography>
      )}
    </Box>
  );
};

export default UserDetailPermissions;